import { BusinessVerificationStage } from './BusinessVerificationV3Models';

type RegistrationInformation = {
    entityName: string | null;
    entityTypesID: number | null;
    entityTypesOther: string | null;
    registrationDate: string | null;
    registrationNumber: string | null;
    officialBusinessRegistrationNumber: string | null;
    taxNumber: string | null;
    doingBusinessAs: string | null;
    addressStreet: string | null;
    addressNumber: string | null;
    addressPostCode: string | null;
    addressCity: string | null;
    addressState: string | null;
    addressCountry: string | null;
    operatingAddressStreet: string | null;
    operatingAddressNumber: string | null;
    operatingAddressPostCode: string | null;
    operatingAddressCity: string | null;
    operatingAddressState: string | null;
    operatingAddressCountry: string | null;
};
type OperationsInformation = {
    webAddress: string | null;
    phoneNumber: string | null;
    supportEmail: string | null;
    bPubliclyListed: string | null;
    ticker: string | null;
    exchanges: string | null;
    operationRegionIds: Array<number>;
    bLicenseRequired: string | null;
    additionalLicensingInfo: string | null;
    primaryRegulator: string | null;
    licenseNumber: string | null;
    financialInstitutionFormFileName: string | null;
    thirdPartyPaymentsFormFileName: string | null;
    businessActivityIds: Array<number>;
    web3ChainsAndAddresses: string | null;
    sourceOfFundsIds: Array<number | string>;
    sourceOfFundsOther: string | null;
    activeBanks: string | null;
    yearlyTransactionsId: any;
    monthlyUsdValueId: any;
    bFundsReceivedUSA: boolean | null;
    bFundsSentUSA: boolean | null;
    // General - API field names
    isPortlProduct?: boolean;
    purposeOfAccountId: number | null;
    purposeOfAccountOther: string | null;
    // Banking & Payments (Fiat) - API field names
    anticipatedMonthlyTransactionCountId: number | null;
    anticipatedMonthlyVolumeUsdId: number | null;
    countriesSendingPaymentsTo: string | null;
    countriesReceivingPaymentsFrom: string | null;
    // Crypto OTC - API field names
    blockchainAddresses: string | null;
    industryType: string | null;
    digitalAssetsTrading: string | null;
    anticipatedMonthlyCryptoTransactionsId: number | null;
    anticipatedMonthlyCryptoVolumeUsdId: number | null;
    cryptoBusinessPlan: string | null;
    // PORTL (Crypto Acquiring) - API field names
    cryptoPaymentTypes: string | null;
    integrationMethodId: number | null;
    expectedMonthlyPaymentVolumeUsdId: number | null;
    averageTransactionSize: string | null;
    settlementPreferenceId: number | null;
    // Merchant Services - API field names
    bAcceptCardPayments: string | null;
    customerTrafficCountries: string | string[] | null;
    historicalChargebackRate: string | null;
    historicalRefundRate: string | null;
    productsServicesSold: string | null;
};

export type BeneficialPersonalOwner = {
    guid: string;
    number: number;
    firstName: string;
    lastName: string;
    title: string;
    dateOfBirth: string;
    nationality: string;
    occupationTypesId: number | null;
    email: string;
    phoneNumber: string;
    country: string;
    ssn: string;
    stateProvince: string;
    city: string;
    addressLine1: string;
    addressLine2: string;
    postcode: string;
    proofOfAddressFilename: string | null;
};
export type BeneficialBusinessOwner = {
    guid: string;
    number: number;
    businessFormationDocumentFilename: string | null;
    companyUboCertificateOfGoodStandingFilename: string | null;
    companyUboW9FormFilename: string | null;
    companyUboW8FormFilename: string | null;
    proofOfAddressFilename: string | null;
} & RegistrationInformation;

export type OwnerTreeStructure = {
    bIsBusiness: boolean;
    ownersGuid: string;
    percentageOwned: number | null;
    bControllingParty: boolean | null;
    positionAtCompany: string | null;
    children: OwnerTreeStructure[] | null;
    isNewNode?: boolean;
    isMainNode?: boolean;
};

type OwnerInformation = {
    exemptionId: number | null;
    individualBeneficialOwners: BeneficialPersonalOwner[];
    businessBeneficialOwners: BeneficialBusinessOwner[];
    beneficialOwnersStructure: OwnerTreeStructure[];
    bConfirmAboveTen: boolean;
    authorizedSigner: string | null;
};
type Terms = {
    bTaxAcknowledgement: boolean;
    bFatcaAcknowledgement: boolean;
    bConfirmTrueAndCorrect: boolean;
    bAcceptTerms: boolean;
    bServiceFeeAcknowledgement: boolean;
    bEConsentDisclosure: boolean;
    bDepositAccountAgreement: boolean;
    bUSAPatriotActNotice: boolean;
};

export interface FormValues {
    businessVerificationStep: BusinessVerificationStage;
    registrationInformation: RegistrationInformation;
    operationsInformation: OperationsInformation;
    ownerInformation: OwnerInformation;
    terms: Terms;
    accountCurrencySelection: number | null;
    documentsUpload: {
        documentTypesID: number | null;
        fileName: string;
        bAlwaysRequired?: boolean;
    }[];
}

export const initialValue: FormValues = {
    businessVerificationStep: BusinessVerificationStage.RegistrationInformation,
    registrationInformation: {
        entityName: null,
        entityTypesID: null,
        entityTypesOther: null,
        registrationDate: null,
        registrationNumber: null,
        officialBusinessRegistrationNumber: null,
        taxNumber: null,
        doingBusinessAs: null,
        addressStreet: null,
        addressNumber: null,
        addressPostCode: null,
        addressCity: null,
        addressState: null,
        addressCountry: null,
        operatingAddressStreet: null,
        operatingAddressNumber: null,
        operatingAddressPostCode: null,
        operatingAddressCity: null,
        operatingAddressState: null,
        operatingAddressCountry: null,
    },
    operationsInformation: {
        webAddress: null,
        phoneNumber: null,
        supportEmail: null,
        bPubliclyListed: null,
        ticker: null,
        exchanges: null,
        operationRegionIds: [],
        bLicenseRequired: null,
        additionalLicensingInfo: null,
        primaryRegulator: null,
        licenseNumber: null,
        thirdPartyPaymentsFormFileName: null,
        financialInstitutionFormFileName: null,
        businessActivityIds: [],
        web3ChainsAndAddresses: null,
        sourceOfFundsIds: [],
        sourceOfFundsOther: null,
        activeBanks: null,
        yearlyTransactionsId: null,
        monthlyUsdValueId: null,
        bFundsReceivedUSA: null,
        bFundsSentUSA: null,
        purposeOfAccountId: null,
        purposeOfAccountOther: null,
        anticipatedMonthlyTransactionCountId: null,
        anticipatedMonthlyVolumeUsdId: null,
        countriesSendingPaymentsTo: null,
        countriesReceivingPaymentsFrom: null,
        // Crypto OTC fields
        blockchainAddresses: null,
        industryType: null,
        digitalAssetsTrading: null,
        anticipatedMonthlyCryptoTransactionsId: null,
        anticipatedMonthlyCryptoVolumeUsdId: null,
        cryptoBusinessPlan: null,
        // PORTL Questions (Crypto Acquiring) fields
        cryptoPaymentTypes: null,
        integrationMethodId: null,
        expectedMonthlyPaymentVolumeUsdId: null,
        averageTransactionSize: null,
        settlementPreferenceId: null,
        // Merchant Services Questions fields
        bAcceptCardPayments: null,
        customerTrafficCountries: null,
        historicalChargebackRate: null,
        historicalRefundRate: null,
        productsServicesSold: null,
    },
    ownerInformation: {
        exemptionId: null,
        bConfirmAboveTen: false,
        individualBeneficialOwners: [],
        businessBeneficialOwners: [],
        authorizedSigner: null,
        beneficialOwnersStructure: [],
    },
    terms: {
        bTaxAcknowledgement: false,
        bFatcaAcknowledgement: false,
        bConfirmTrueAndCorrect: false,
        bAcceptTerms: false,
        bServiceFeeAcknowledgement: false,
        bEConsentDisclosure: false,
        bDepositAccountAgreement: false,
        bUSAPatriotActNotice: false,
    },
    accountCurrencySelection: null,
    documentsUpload: [
        {
            documentTypesID: null,
            fileName: '',
        },
    ],
};

export const generateBeneficialPersonalOwner = (
    guid: string,
    number: number
): BeneficialPersonalOwner => {
    return {
        guid,
        number,
        firstName: '',
        lastName: '',
        title: '',
        dateOfBirth: '',
        nationality: '',
        occupationTypesId: null,
        email: '',
        phoneNumber: '',
        country: '',
        ssn: '',
        stateProvince: '',
        city: '',
        addressLine1: '',
        addressLine2: '',
        postcode: '',
        proofOfAddressFilename: null,
    };
};

export const generateBeneficialBusinessOwner = (
    guid: string,
    number: number
): BeneficialBusinessOwner => {
    return {
        guid,
        number,
        businessFormationDocumentFilename: null,
        companyUboCertificateOfGoodStandingFilename: null,
        companyUboW9FormFilename: null,
        companyUboW8FormFilename: null,
        proofOfAddressFilename: null,
        entityName: null,
        entityTypesID: null,
        entityTypesOther: null,
        registrationDate: null,
        registrationNumber: null,
        officialBusinessRegistrationNumber: null,
        taxNumber: null,
        doingBusinessAs: null,
        addressStreet: null,
        addressNumber: null,
        addressPostCode: null,
        addressCity: null,
        addressState: null,
        addressCountry: null,
        operatingAddressStreet: null,
        operatingAddressNumber: null,
        operatingAddressPostCode: null,
        operatingAddressCity: null,
        operatingAddressState: null,
        operatingAddressCountry: null,
    };
};

// Utility functions to normalize beneficial owners data
export const normalizeBeneficialOwnersNumbers = <T extends { number: number }>(
    owners: T[]
): T[] => {
    return owners.map((owner, index) => ({
        ...owner,
        number: index + 1,
    }));
};

export const normalizeOwnerInformationData = (ownerInformation: any) => {
    if (!ownerInformation) return ownerInformation;

    return {
        ...ownerInformation,
        individualBeneficialOwners: normalizeBeneficialOwnersNumbers(
            ownerInformation.individualBeneficialOwners || []
        ),
        businessBeneficialOwners: normalizeBeneficialOwnersNumbers(
            ownerInformation.businessBeneficialOwners || []
        ),
    };
};

/** Resolve Monthly USD Value from V3 field or legacy monthlyUsdValueId (API load). */
export const resolveMonthlyVolumeUsdId = (
    operationsInformation: FormValues['operationsInformation'] | null | undefined
): number | null => {
    if (!operationsInformation) return null;
    const raw =
        operationsInformation.anticipatedMonthlyVolumeUsdId ??
        operationsInformation.monthlyUsdValueId;
    if (raw === null || raw === undefined || raw === '') return null;
    const parsed = Number(raw);
    return Number.isNaN(parsed) ? null : parsed;
};

/** Merge legacy monthlyUsdValueId into anticipatedMonthlyVolumeUsdId; drop legacy from form state. */
export const normalizeOperationsInformationData = (
    operationsInformation: FormValues['operationsInformation'] | null | undefined
): FormValues['operationsInformation'] => {
    const ops = operationsInformation ?? initialValue.operationsInformation;
    const anticipatedMonthlyVolumeUsdId = resolveMonthlyVolumeUsdId(ops);

    return {
        ...ops,
        anticipatedMonthlyVolumeUsdId,
        monthlyUsdValueId: null,
    };
};
